import React from 'react';

import { Button, Flex, List, message, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { isForbiddenError } from '@app/services/apiErrors';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';

import { useGetSwitchedOffCatalogsQuery } from './hooks';

interface IProps {
  warehouseUids?: string[];
  onClose: () => void;
}

export const SwitchedOffCatalogsModal: React.FC<IProps> = ({ warehouseUids, onClose }) => {
  const navigate = useNavigate();
  const warehousesT = useTranslation(NSKeys.warehouses);
  const getErrorMessage = useApiErrorMessage();
  const catalogs = useGetSwitchedOffCatalogsQuery(warehouseUids);
  const items = catalogs.data?.items || [];

  React.useEffect(() => {
    if (catalogs.isError) {
      if (!isForbiddenError(catalogs.error)) {
        message.error(getErrorMessage(catalogs.error));
      }
      onClose();
    } else if (catalogs.isSuccess && !items.length) {
      onClose();
    }
  }, [catalogs.isError, catalogs.isSuccess, items.length]);

  return (
    <Modal
      open={!!warehouseUids?.length && !!items.length}
      title={warehousesT.t('switchedOffCatalogs.title')}
      onCancel={onClose}
      footer={
        <Button type="primary" onClick={onClose}>
          {warehousesT.t('switchedOffCatalogs.close')}
        </Button>
      }
      destroyOnHidden
    >
      <Flex vertical gap={12}>
        <Typography.Text>{warehousesT.t('switchedOffCatalogs.text')}</Typography.Text>
        <List
          size="small"
          dataSource={items}
          style={{ maxHeight: 320, overflowY: 'auto' }}
          renderItem={(catalog) => (
            <List.Item
              actions={
                catalog.$permissions?.update
                  ? [
                      <Typography.Link key="open" onClick={() => navigate(`/dashboard/catalogs/edit/${catalog.uid}`)}>
                        {warehousesT.t('switchedOffCatalogs.open')}
                      </Typography.Link>,
                    ]
                  : undefined
              }
            >
              <List.Item.Meta title={catalog.name} description={catalog.warehouse?.name} />
            </List.Item>
          )}
        />
      </Flex>
    </Modal>
  );
};
